import Vue from 'vue';
import { i18n } from '@/i18n';
import { defineStore } from 'pinia';
import { useBranchStore } from '@/Stores/branch';
import { useUserStore } from '@/Stores/user';
import { axios } from '@/Services/Backend';

const createNewCart = (userID, branchID) => {
    return {
        userID,
        branchID,

        deliveryAddress: null,
        observation: null,

        isBudgetRequest: false,

        isScheduledOrder: false,
        scheduledOrderDate: null,

        isLaboratoryOrder: false,

        isFirstOrder: false,
        firstOrderDiscount: {},

        selectedAdditionalInfo: [],

        products: [],
    };
};

export const useAffinityOrderStore = defineStore('affinityOrder', {
    state: () => ({
        // @Note: This is equivalent to `Map<String, Cart>`, we can't use the `Map` class in Vue 2 as it's not reactive.
        //        In which the string (key) is the result of concatenating: 'UserID + branchID', to get an unique identifier.
        shoppingCarts: {},
        tabs: [],
        subtotals: [],
        maxQuantity: 99999,
    }),

    actions: {
        /// @Internal Returns a string with the: user ID + branch ID, e.g. '5_1_0'.
        _uniqueCartIdentifier() {
            const branchID = this._getCart.branchID;
            const userID = this._getCart.userID;

            return `${userID}_${branchID}`;
        },

        /// This function will get called every time a user/branch is changed (after the event).
        /// It allows us to update the properties that are associated with the user/branch.
        reloadFromLocalStorage() {
            if (this._getCart.branchID == undefined) return;

            this.readProducts();
            this.readDeliveryAddress();
            this.readObservation();
            this.readAdditionalInfo();
            this.getLatestProductsData();
        },

        //
        // Delivery Address & Observation
        //
        readDeliveryAddress() {
            const identifier = this._uniqueCartIdentifier();

            const deliveryAddress = localStorage.getItem(`deliveryAddressAffinity_${identifier}`);
            if (!deliveryAddress) return;

            this._getCart.deliveryAddress = JSON.parse(deliveryAddress);
        },

        setDeliveryAddress(deliveryAddress) {
            this._getCart.deliveryAddress = deliveryAddress;

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`deliveryAddressAffinity_${identifier}`, JSON.stringify(deliveryAddress));
        },

        readObservation() {
            const identifier = this._uniqueCartIdentifier();

            const observation = localStorage.getItem(`observationAffinity_${identifier}`);
            if (!observation) return;

            this._getCart.observation = JSON.parse(observation);
        },

        setObservation(observation) {
            this._getCart.observation = observation;

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`observationAffinity_${identifier}`, JSON.stringify(observation));
        },

        readAdditionalInfo() {
            const identifier = this._uniqueCartIdentifier();

            const selectedAdditionalInfo = localStorage.getItem(`orderAdditionalInfoAffinity_${identifier}`);
            const firstOrderDiscount = localStorage.getItem(`orderAdditionalInfoAffinity_discount_${identifier}`);
            const scheduledOrderDate = localStorage.getItem(`orderAdditionalInfoAffinity_date_${identifier}`);
            if (!selectedAdditionalInfo) return;
            this._getCart.selectedAdditionalInfo = JSON.parse(selectedAdditionalInfo);

            if (scheduledOrderDate) {
                this._getCart.scheduledOrderDate = scheduledOrderDate;
            }
            if (firstOrderDiscount) {
                this._getCart.firstOrderDiscount = JSON.parse(firstOrderDiscount);
            }
        },

        setAdditionalInfo(selectedAdditionalInfo, date, discount) {
            this.setSelectedAdditionalInfo(selectedAdditionalInfo);
            this.setFirstOrderDiscount(discount);
            this.setScheduledOrderDate(date);
        },

        setUncheckAdditionalInfo() {
            this.setIsBudgetRequest(false, false);
            this.setIsScheduledOrder(false);
            this.setIsLaboratoryOrder(false);
            this.setIsFirstOrder(false);
        },

        //
        // Cart / Products
        //
        addProduct(product) {
            if (this.getProduct(product.id)) {
                throw new Error(`The product ${product.id} already exists in the cart.`);
            }

            this._getCart.products.push(product);

            this.saveCart();
        },

        removeProduct(id) {
            const index = this.getIndexOfProductWithID(id);
            this._getCart.products.splice(index, 1);

            this.saveCart();
        },

        incrementProductQuantity(id, amount) {
            const item = this.getProduct(id);

            let currentQuantity = item.quantity;
            currentQuantity += amount;

            if (currentQuantity < 1) {
                currentQuantity = 1;
            }

            if (currentQuantity > this.maxQuantity) {
                currentQuantity = this.maxQuantity;
            }

            item.quantity = currentQuantity;
            this.saveCart();
        },

        setProductQuantity(id, quantity) {
            const item = this.getProduct(id);

            item.quantity = quantity > 0 ? quantity : 0;
            this.saveCart();
        },

        readProducts() {
            const identifier = this._uniqueCartIdentifier();
            const products = localStorage.getItem(`productsAffinity_${identifier}`);

            this._getCart.products = JSON.parse(products) ?? [];
        },

        saveCart() {
            const cart = this._getCart.products;

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`productsAffinity_${identifier}`, JSON.stringify(cart));

            this.setSelectedAdditionalInfo();
            this.setFirstOrderDiscount();
        },

        getLatestProductsData() {
            const productIds = this._getCart.products?.map((p) => p.id);
            if (!productIds.length) return;

            let params = {
                forAffinity: true,
                products: JSON.stringify(productIds),
            };

            axios
                .get(`/api/branches/${this._getCart.branchID}/refresh-cart-data`, { params })
                .then(({ data }) => {
                    if (!data) return;

                    data.forEach((p) => {
                        const currentProduct = this.getProduct(p.id);
                        if (currentProduct) currentProduct.image = p.image;
                    });
                })
                .catch();
        },

        clearCart(brand = '') {
            if (brand === '') {
                // @Note: Clear the cart by Geral tab
                this._getCart.products.splice(0);
                this.saveCart();
            } else {
                // @Note: Clear the cart by specific tab (!= Geral)
                // @Note: The .map() is necessary so that the iterator isn't invalidated when removing.
                this._getCart.products
                    .filter((product) => {
                        return product.brand === brand;
                    })
                    .map((product) => product.id)
                    .forEach((product) => this.removeProduct(product));
            }
        },

        fullClearCart() {
            this._getCart.products = [];
            this.saveCart();
        },

        clearCartAndNotify(brand, showSuccess) {
            this.clearCart(brand);

            const brandForToast = brand != '' ? brand : 'GERAL';
            showSuccess(i18n.t('toasts.brandQuantitiesCleared', { brand: brandForToast }));
        },

        fullClearCartAndNotify(showSuccess) {
            this.fullClearCart();

            showSuccess(i18n.t('toasts.quantitiesCleared'));
        },

        finishPurchase(showSuccess) {
            this.clearCart();

            this.setDeliveryAddress(null);
            this.setObservation(null);
            this.setAdditionalInfo(null, null, null);
            this.setUncheckAdditionalInfo();

            showSuccess(i18n.t('toasts.orderPlaced'));
        },

        ///
        /// Cart additional info
        ///
        setSelectedAdditionalInfo(options) {
            // If no new values are sent, will only refresh the data in the local storage
            if (options !== undefined) {
                this._getCart.selectedAdditionalInfo = options;
            }

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`orderAdditionalInfoAffinity_${identifier}`, JSON.stringify(this._getCart.selectedAdditionalInfo));
        },

        setIsBudgetRequest(isRequest, showWarning = true) {
            this._getCart.isBudgetRequest = isRequest;

            if (isRequest) {
                if (this._getCart.isScheduledOrder || this._getCart.isLaboratoryOrder || this._getCart.isFirstOrder) {
                    showWarning(i18n.t('toasts.quoteIncompatible'), 8000);
                }

                this.setIsScheduledOrder(false);
                this.setIsLaboratoryOrder(false);
                this.setIsFirstOrder(false);
            } else {
                if (!this._getCart.selectedAdditionalInfo) return;

                let index = this._getCart.selectedAdditionalInfo.findIndex((option) => option.key == 0);
                if (index != -1) {
                    this._getCart.selectedAdditionalInfo.splice(index, 1);
                }
            }

            this.setSelectedAdditionalInfo();
        },

        setIsScheduledOrder(isRequest) {
            this._getCart.isScheduledOrder = isRequest;

            if (!isRequest) {
                // If not selected, also clear the values related to it
                this.setScheduledOrderDate('');

                if (!this._getCart.selectedAdditionalInfo) return;

                let index = this._getCart.selectedAdditionalInfo.findIndex((option) => option.key == 1);
                if (index != -1) {
                    this._getCart.selectedAdditionalInfo.splice(index, 1);
                }
            }

            this.setSelectedAdditionalInfo();
        },

        setScheduledOrderDate(newDate) {
            // If no new values are sent, will only refresh the data in the local storage
            if (newDate !== undefined) {
                this._getCart.scheduledOrderDate = newDate;
            }

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`orderAdditionalInfoAffinity_date_${identifier}`, this._getCart.scheduledOrderDate);
        },

        setIsLaboratoryOrder(isRequest) {
            this._getCart.isLaboratoryOrder = isRequest;

            if (!isRequest) {
                if (!this._getCart.selectedAdditionalInfo) return;

                let index = this._getCart.selectedAdditionalInfo.findIndex((option) => option.key == 2);
                if (index != -1) {
                    this._getCart.selectedAdditionalInfo.splice(index, 1);
                }
            }

            this.setSelectedAdditionalInfo();
        },

        setIsFirstOrder(isRequest) {
            this._getCart.isFirstOrder = isRequest;

            if (!isRequest) {
                // If not selected, also clear the values related to it
                this.setFirstOrderDiscount(null);

                if (!this._getCart.selectedAdditionalInfo) return;

                let index = this._getCart.selectedAdditionalInfo.findIndex((option) => option.key == 3);
                if (index != -1) {
                    this._getCart.selectedAdditionalInfo.splice(index, 1);
                }
            }

            this.setSelectedAdditionalInfo();
        },

        setFirstOrderDiscount(discount) {
            // If no new values are sent, will only refresh the data in the local storage
            if (discount !== undefined) {
                this._getCart.firstOrderDiscount = discount;
            }

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`orderAdditionalInfoAffinity_discount_${identifier}`, JSON.stringify(this._getCart.firstOrderDiscount));
        },

        setProductPackages(id, packages) {
            const item = this.getProduct(id);
            if (item) {
                Vue.set(item, 'packages', packages);
                this.saveCart();
            }
        },

        setProductPallets(id, pallets) {
            const item = this.getProduct(id);
            if (item) {
                Vue.set(item, 'pallets', pallets);
                this.saveCart();
            }
        },

        applyShoppingCartItemDiscount(id, discount_num, discount, productOverride = null) {
            // productOverride is used to apply discounts to products outside of the cart, like
            // the affinity page datatable listing for products with 0 quantity
            const item = this.getProduct(id);

            switch (discount_num) {
            case 1:
                Vue.set(item ? item : productOverride, 'discount1', discount);
                break;
            case 2:
                Vue.set(item ? item : productOverride, 'discount2', discount);
                break;
            case 3:
                Vue.set(item ? item : productOverride, 'discount3', discount);
                break;
            case 4:
                Vue.set(item ? item : productOverride, 'discount4', discount);
                break;
            case 5:
                Vue.set(item ? item : productOverride, 'discount5', discount);
                break;
            case 6:
                Vue.set(item ? item : productOverride, 'discount6', discount);
                break;
            default:
            }

            this.saveCart();
        },

        setProductDiscountedValue(id, value) {
            const item = this.getProduct(id);

            if (item) {
                Vue.set(item, 'discounted_price', value);
                this.saveCart();
            }
        },

        setTabs(tabs) {
            this.tabs = tabs;
            localStorage.setItem('affinityTabs', JSON.stringify(this.tabs));
        },

        setSubTotals(subtotals) {
            this.subtotals = subtotals;
        },

        addSubTotal(newRow) {
            this.subtotals.push(newRow);
        },

        updateSubTotal(index, newRow) {
            this.subtotals[index] = newRow;
        },

        removeAllSubTotals() {
            this.subtotals.splice(0);
        },

        updateProductPrices(updatedProducts) {
            for (const product of updatedProducts) {
                const currentProduct = this.getProduct(product.id);
                currentProduct.price = product.price;
            }

            this.saveCart();
        },
    },

    getters: {
        /// @Internal This returns the cart for the currently selected branch, this function should
        ///            not be used directly. Use one of the other getter functions below instead.
        _getCart: (state) => {
            const userID = useUserStore().user?.id;
            const branchID = useBranchStore().branch?.id;

            const cartIdentifier = `${userID}_${branchID}`;

            const exists = userID !== null && branchID !== null;
            const existsInCart = cartIdentifier in state.shoppingCarts;

            if (!exists || !existsInCart) {
                // It might not exist if it's the first time that the user is trying
                //  to purchase products for that branch in the current session.
                const cart = createNewCart(userID, branchID);

                Vue.set(state.shoppingCarts, cartIdentifier, cart);
            }

            return state.shoppingCarts[cartIdentifier];
        },

        getProducts: (state) => state._getCart.products,
        getProduct: (state) => (id) => state.getProducts.find((item) => item.id === id),
        getIndexOfProductWithID: (state) => (id) => state.getProducts.indexOf(state.getProduct(id)),
        getProductCount: (state) => state.getProducts.length,
        getProductSum: (state) => (fn) => state.getProducts.reduce((acc, item) => acc + fn(item), 0),

        getProductQuantity: (state) => (item) => {
            const product = state.getProduct(item.id);
            return !product ? 0 : product.quantity;
        },

        getDeliveryAddress: (state) => state._getCart?.deliveryAddress,
        getObservation: (state) => state._getCart?.observation,

        getProductDiscount:
            (state) =>
                (item, discount_num, forSingleQuantity = false) => {
                    const product = state.getProduct(item.id);

                    if (forSingleQuantity) {
                        switch (discount_num) {
                        case 1:
                            return item.discount1;
                        case 2:
                            return item.discount2;
                        case 3:
                            return item.discount3;
                        case 4:
                            return item.discount4;
                        case 5:
                            return item.discount5;
                        case 6:
                            return item.discount6;
                        default:
                            return 0;
                        }
                    }

                    if (product) {
                        switch (discount_num) {
                        case 1:
                            return product.discount1;
                        case 2:
                            return product.discount2;
                        case 3:
                            return product.discount3;
                        case 4:
                            return product.discount4;
                        case 5:
                            return product.discount5;
                        case 6:
                            return product.discount6;
                        default:
                            return 0;
                        }
                    }
                    return 0;
                },

        //Additional info
        isBudgetRequest: (state) => state._getCart.isBudgetRequest,
        isScheduledOrder: (state) => state._getCart.isScheduledOrder,
        getScheduledOrderDate: (state) => state._getCart.scheduledOrderDate,
        isLaboratoryOrder: (state) => state._getCart.isLaboratoryOrder,
        isFirstOrder: (state) => state._getCart.isFirstOrder,
        getFirstOrderDiscount: (state) => state._getCart?.firstOrderDiscount,
        selectedAdditionalInfo: (state) => state._getCart.selectedAdditionalInfo,

        getTabs: (state) => state.tabs,
        getTabByIndex: (state) => (index) => state.tabs[index],
        getProductsByEncType: (state) => (encType) => state.getProducts.filter((item) => item.enc_type === encType),
        getProductsByBrand: (state) => (brand) => state.getProducts.filter((item) => item.brand === brand),
        getSubTotals: (state) => state.subtotals,
    },
});
